import { getValue } from '@Selectors';
import {
	getSensorHealthTrend,
	getSensorHealthTrendLegacy,
} from '@Services/edgeAnalytics';
import { useGlobalization } from '@Translations';
import { UpTimePercentage } from '@Types/edgeAnalytics.types';
import { mapUptimeDataRequest } from '@Utils/sensorsAnalytics';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

export enum UptimeLabels {
	'1HOUR' = '1 Hour',
	'8HOUR' = '8 Hours',
	'24HOUR' = '24 Hours',
	'7DAY' = '7 Days',
}

const defaultUptimes: Record<keyof typeof UptimeLabels, string> = {
	'1HOUR': '0%',
	'8HOUR': '0%',
	'24HOUR': '0%',
	'7DAY': '0%',
};

interface HealthTrendProps {
	sensorId: string;
	isSensorHealthV3ApiEnabled: boolean;
	unloadLagMinutes: string;
}

export const useConfigurationHealthTrend = ({
	sensorId,
	isSensorHealthV3ApiEnabled,
	unloadLagMinutes,
}: HealthTrendProps) => {
	const {
		configurations: { list },
		systemConnections: { list: systemConnections },
	} = useSelector(getValue);
	const [
		selectedSensorHealthPercentages,
		setSelectedSensorHealthPercentages,
	] = useState({} as Record<keyof typeof UptimeLabels, string>);

	const { t } = useGlobalization();

	useEffect(() => {
		if (sensorId) {
			if (isSensorHealthV3ApiEnabled) {
				fetchSensorHealthTrend(sensorId);
			} else {
				fetchSensorHealthTrendLegacy(sensorId);
			}
		} else {
			setSelectedSensorHealthPercentages(
				{} as Record<keyof typeof UptimeLabels, string>,
			);
		}
	}, [sensorId]);

	const fetchSensorHealthTrend = useCallback(
		async (sensorId: string) => {
			const sensor = list.find(sensor => sensor.sensorId === sensorId);
			if (sensor) {
				const sensors = mapUptimeDataRequest(
					systemConnections,
					[sensor],
					Number.parseInt(unloadLagMinutes || '0'),
				);

				const { query } = (await getSensorHealthTrend(sensors)).data;
				if (query?.length) {
					const uptimePercentages = query[0].upTimePercentages;
					setSelectedSensorHealthPercentages(
						getHealthPercentages(uptimePercentages),
					);
				}
			}
		},
		[list, systemConnections],
	);

	/**
	 * @ deprecated
	 */
	const fetchSensorHealthTrendLegacy = useCallback(
		async (sensorId: string) => {
			const { query } = (await getSensorHealthTrendLegacy(sensorId)).data;
			if (query?.length) {
				const uptimePercentages = query[0].upTimePercentages;
				setSelectedSensorHealthPercentages(
					getHealthPercentages(uptimePercentages),
				);
			}
		},
		[],
	);

	const getHealthPercentages = useCallback(
		(uptimePercentages: UpTimePercentage[]) => {
			if (uptimePercentages && uptimePercentages.length) {
				const result = { ...defaultUptimes };

				uptimePercentages.forEach(trend => {
					const label = (trend.sliceLength +
						trend.timePart) as keyof typeof UptimeLabels;
					result[label] = `${Math.round(+trend.value * 100)}%`;
				});

				return result;
			}

			return {} as Record<keyof typeof UptimeLabels, string>;
		},
		[],
	);

	const getTranslatedLabel = (key: keyof typeof UptimeLabels) => {
		return t(UptimeLabels[key]);
	};

	return {
		selectedSensorHealthPercentages,
		getTranslatedLabel,
	};
};
